import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { UpcomingQuestion } from '@shared/types';
import { serverNow } from '@/lib/api';
import { BUSY_MESSAGE, isSinglePick, payloadFor, payloadOf, sendAnswer, type AnswerPayload } from '@/lib/answer';
import { isOnline, useOnline } from '@/lib/online';
import { useGame, type MyAnswer } from '@/store/game';
import { sfx } from '@/audio/sfx';
import { haptic } from '@/lib/haptics';
import { Countdown } from '@/components/Countdown';
import { AppHeader } from '@/components/AppHeader';
import { Stamp } from '@/components/Stamp';
import { Pulse, type PulseMood } from '@/brand/Pulse';
import { usePreloadMascot } from '@/brand/MascotSlot';
import { PulseVignette } from '@/motion';
import { FormatView } from '@/formats';
import type { LiveQuestion } from '@/formats/types';
import { answerHint } from '@/formats/names';
import { IntroCard, stampFor } from './parts/IntroCard';
import { Screen } from './parts/Screen';
import { useRerenderAt, useSecondTicker, useCalm } from './parts/time';
import { COPY } from './parts/copy';
import { useOwnSnapshot, isSameQuestion } from './parts/frozen';
import { secondCue } from './parts/moments';
import { seconds } from './parts/kudosData';

/** The answer this phone has in for question `index`: a lock-in whose request is on its way, accepted or shut out; a lock-in that never got there counts as nothing in. */
function heldAnswer(a: MyAnswer | undefined, index: number): MyAnswer | undefined {
  return a && a.index === index && a.send !== 'failed' ? a : undefined;
}

/** 17 Oct 2026: a delayed question before its text arrives: the stub's format, round and clock, for the intro card only. */
function introOf(u: UpcomingQuestion): LiveQuestion {
  return { id: '', format: u.format, stem: '', options: [], durationMs: u.deadlineAt - u.startsAt, maxPoints: 0, startsAt: u.startsAt, deadlineAt: u.deadlineAt, ...(u.round ? { round: u.round } : {}) };
}

/** M3 to M5: the pre-roll intro card, then the question with the countdown, the heartbeat in the last seconds, and the lock-in. */
export default function Question() {
  const snap = useOwnSnapshot(isSameQuestion)!; const q = snap.question ?? introOf(snap.upcoming!);
  usePreloadMascot(q.category);
  const session = useGame((s) => s.session);
  const myAnswer = useGame((s) => s.myAnswer);
  const reduce = useCalm();
  useRerenderAt(q.startsAt, q.deadlineAt);

  const now = serverNow();
  // With a stream delay the intro stays on until the text arrives, even past the start (the same card, never mounted twice).
  const preroll = !snap.question || now < q.startsAt;
  const expired = now > q.deadlineAt;
  const locked = myAnswer?.index === snap.index ? myAnswer : undefined;
  // 9 Oct: the lock is optimistic, so the send state says what the screen shows: on its way (a quiet cue), in (the
  // stamp), late (the close) or failed (the options open again for another tap, same or different).
  const held = heldAnswer(myAnswer, snap.index);
  const [left, setLeft] = useState(() => Math.max(0, Math.ceil((q.deadlineAt - now) / 1000)));
  const [err, setErr] = useState<string>();
  const inFlight = useRef(false);
  const stamp = stampFor(q, snap.index, snap.total);

  // M3: a whoosh as the intro card lands; the defib charge for double points.
  const introPlayed = useRef(-1);
  useEffect(() => {
    if (introPlayed.current === snap.index || serverNow() >= q.startsAt) return;
    introPlayed.current = snap.index;
    sfx.play(stamp === 'double' ? 'charge' : 'whoosh');
  }, [snap.index, q.startsAt, stamp]);

  // M4: a tick each second, the heartbeat in the last five, a haptic tick in the last three. Calm once you have locked in.
  useSecondTicker(q.startsAt, q.deadlineAt, (k) => {
    setLeft(k);
    const cue = secondCue(k, Boolean(heldAnswer(useGame.getState().myAnswer, snap.index)));
    if (cue.sound) sfx.play(cue.sound);
    if (cue.haptic) haptic('tick');
  });

  // One POST per answer (lib/answer.ts), and the send state follows how it ended: in (with the server's own answer when
  // it already held one: a reply lost on the way, then a different tap, ends with the pick the server kept), late (the
  // window shut on it), or failed (no answer, no connection, or a refusal of the answer itself), when the options open
  // for another tap. The seat moving to another device is the game page's to act on; here the lock just lets go.
  async function send(answer: AnswerPayload) {
    const token = session?.token; if (!token || inFlight.current) return;
    inFlight.current = true; setErr(undefined);
    useGame.getState().markSend(snap.index, 'sending');
    const r = await sendAnswer({ code: snap.code, token, index: snap.index, answer, deadlineAt: q.deadlineAt });
    inFlight.current = false;
    const g = useGame.getState();
    if (r.ok) g.confirmAnswer(snap.index, r.elapsedMs, r.duplicate ? r.answer : undefined);
    else if (r.reason === 'late') { g.markSend(snap.index, 'late'); setErr(r.message); }
    else if (r.reason === 'refused') { g.markSend(snap.index, 'failed'); setErr(r.message); }
    else if (r.reason === 'moved') g.markSend(snap.index, 'failed');
    else { g.markSend(snap.index, 'failed'); setErr(COPY.question.notSent); }
  }

  /** Every format answers here: { optionId } for one pick, { value }, { optionIds } or { order } for the rest. */
  function choose(raw: AnswerPayload | string) {
    if (held || expired || preroll) return;
    const answer = payloadFor(q, raw); if (!answer) return;
    useGame.getState().lockAnswer(snap.index, answer);
    // The lock-in cue only when the tap can go: with no connection it is not sent, and "Not sent" follows at once.
    if (isOnline()) { sfx.play('lock'); haptic('lock'); }
    void send(answer);
  }

  // 9 Oct: a tap made with no connection is not sent (lib/answer.ts). When the connection comes back while the question
  // is still open, the last tap goes once by itself; if that fails too, it is the player's tap again.
  const online = useOnline();
  const wasOffline = useRef(false);
  useEffect(() => {
    if (!online) { wasOffline.current = true; return; }
    if (!wasOffline.current) return;
    wasOffline.current = false;
    const a = useGame.getState().myAnswer;
    if (!a || a.index !== snap.index || a.send !== 'failed' || serverNow() > q.deadlineAt) return;
    const answer = payloadOf(a);
    if (answer) void send(answer);
  }, [online]); // eslint-disable-line react-hooks/exhaustive-deps

  const lockedS = held ? seconds(held.elapsedMs ?? Math.max(0, held.lockedAt - q.startsAt)) : undefined;
  const single = isSinglePick(q);
  // Said once: where a tap locks the answer, the footer gives that rule; number, order and pick-all carry their own Lock
  // in and say nothing here. The intro card has just named the format and how it works, and shown its category and any
  // stamp, so this screen does not say them again.
  const hint = answerHint(q);
  const pressure = !held && !expired && left <= 5;
  const mood: PulseMood = expired && !held ? 'flat' : pressure ? 'racing' : 'steady';
  const enter = reduce ? { opacity: 0 } : { opacity: 0, y: 24 };
  // The window shut on the answer: the server said so, or a tap that never got through has run out of clock. Both look
  // the same: the pick shown but dimmed, nothing stamped, the options closed, and the close said once as the alert.
  const late = held?.send === 'late' || (locked?.send === 'failed' && expired);
  const notice = late ? (held?.send === 'late' ? err : BUSY_MESSAGE) : err;
  const shown = held ?? (late ? locked : undefined);
  const shownPayload = shown ? payloadOf(shown) : undefined;

  return (
    <AnimatePresence mode="popLayout">
      {preroll ? (
        <motion.div key="intro" data-phase="question" className="min-h-full" exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: -24 }} transition={{ duration: 0.22 }}>
          <IntroCard snap={snap} q={q} playerId={session?.playerId} />
        </motion.div>
      ) : (
        <motion.div key="ask" className="min-h-full" initial={enter} animate={{ opacity: 1, y: 0 }} transition={{ type: 'spring', stiffness: 380, damping: 32 }}>
          <Screen phase="question" label={COPY.question.of(snap.index, snap.total)} header={<AppHeader progress={{ index: snap.index, total: snap.total }} />}>
            <div className="flex items-center gap-4">
              <div className={`min-w-0 flex-1 ${pressure ? 'text-wrong' : 'text-brand'}`}>
                <Pulse mood={mood} width="100%" height={30} tick={held?.lockedAt} />
              </div>
              <Countdown startsAt={q.startsAt} deadlineAt={q.deadlineAt} />
            </div>
            {/* 15 Oct 2026: the answer's status sits under the clock, not after the options: on a phone a long list or a clue
                stack pushed "Locked in" off the bottom of the screen. */}
            <div data-part="answer-status" className="flex min-h-11 flex-col items-center justify-center gap-2" aria-live="polite">
              <AnimatePresence mode="wait" initial={false}>
                {held?.send === 'sent' ? (
                  // M5: the answer is stamped in (a rubber stamp, not a pill).
                  <span key="locked" className="flex justify-center">
                    <Stamp tone="ink" tilt={-3}>{COPY.question.locked(lockedS!)}</Stamp>
                  </span>
                ) : held?.send === 'sending' ? (
                  <motion.p key="sending" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center text-ink-3">{COPY.question.sending}</motion.p>
                ) : notice ? null : expired ? (
                  <motion.p key="expired" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-semibold text-ink-2">{COPY.question.expired}</motion.p>
                ) : hint ? (
                  <motion.p key="tap" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center text-ink-3">{hint}</motion.p>
                ) : null}
              </AnimatePresence>
              {notice && <p role="alert" className="text-center text-sm font-semibold text-wrong">{notice}</p>}
            </div>
            <div data-part="answer-area" data-late={late ? '' : undefined} className={late ? 'opacity-60 transition-opacity' : undefined}>
              <FormatView question={q} phase={shown ? 'locked' : 'answering'} myOptionId={single ? shown?.optionId || undefined : undefined}
                myAnswer={shownPayload} display={snap.display} disabled={Boolean(held) || expired} onAnswer={choose} />
            </div>
          </Screen>
          <PulseVignette active={pressure} bpm={left <= 3 ? 140 : 110} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
